import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Search, Phone, Heart, MapPin, Menu, X, ChevronDown, MessageCircle } from "lucide-react";
import { CSLogo } from "@/components/CSLogo";
import { useShortlist } from "@/context/ShortlistContext";
import { api, resolveContact } from "@/lib/api";
import { buildWhatsAppLink } from "@/lib/format";

const navLinks = [
  { to: "/buy", label: "Buy Cars", testid: "nav-buy" },
  { to: "/sell", label: "Sell Car", testid: "nav-sell" },
  { to: "/loans", label: "Loans", testid: "nav-loans" },
];
const moreLinks = [
  { to: "/about", label: "About Carz Swag" },
  { to: "/certified", label: "CS Certified" },
  { to: "/reviews", label: "Customer Reviews" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact Us" },
];

const PRICE_OPTS = [
  { label: "Under ₹5L", qs: { price_max: 500000 } },
  { label: "₹5L – ₹10L", qs: { price_min: 500000, price_max: 1000000 } },
  { label: "₹10L – ₹15L", qs: { price_min: 1000000, price_max: 1500000 } },
  { label: "₹15L – ₹20L", qs: { price_min: 1500000, price_max: 2000000 } },
  { label: "₹20L+", qs: { price_min: 2000000 } },
];
const FUEL_OPTS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANS_OPTS = ["Manual", "Automatic", "AMT", "CVT", "DCT", "Other"];
const KM_OPTS = [
  { label: "Under 20K", qs: { km_max: 20000 } },
  { label: "20K – 40K", qs: { km_min: 20000, km_max: 40000 } },
  { label: "40K – 60K", qs: { km_min: 40000, km_max: 60000 } },
  { label: "60K – 80K", qs: { km_min: 60000, km_max: 80000 } },
  { label: "80K – 1L", qs: { km_min: 80000, km_max: 100000 } },
  { label: "1L – 1.5L", qs: { km_min: 100000, km_max: 150000 } },
  { label: "1.5L+", qs: { km_min: 150000 } },
];

const Pop = ({ label, children, testid, color = "red" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const btnCls = color === "red"
    ? `inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-bold border transition-all ${open ? "bg-white text-[#940C2C] border-white" : "bg-transparent text-white border-white/40 hover:bg-white hover:text-[#940C2C] hover:border-white"}`
    : `inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold border transition-all ${open ? "bg-[#F6E6EB] border-[#940C2C] text-[#940C2C]" : "border-gray-200 text-gray-800 hover:border-gray-900"}`;
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} data-testid={testid} className={btnCls}>{label} <ChevronDown size={12} /></button>
      {open && <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-50 min-w-[220px]" onClick={() => setOpen(false)}>{children}</div>}
    </div>
  );
};

const MakeModelPop = ({ makes, modelsByMake, onApply, testid }) => {
  const [open, setOpen] = useState(false);
  const [make, setMake] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-bold border transition-all ${open ? "bg-white text-[#940C2C] border-white" : "bg-transparent text-white border-white/40 hover:bg-white hover:text-[#940C2C] hover:border-white"}`}>
        Make & Model <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[420px] flex max-h-80 overflow-hidden">
          <div className="w-1/2 border-r border-gray-100 overflow-y-auto">
            {makes.map((m) => (
              <button key={m} onClick={() => setMake(m)}
                className={`block w-full text-left px-3 py-2 text-sm font-medium ${make === m ? "bg-[#F6E6EB] text-[#940C2C]" : "text-gray-700 hover:bg-gray-50"}`}>
                {m}
              </button>
            ))}
          </div>
          <div className="w-1/2 overflow-y-auto">
            {!make && <div className="p-4 text-xs text-gray-400">Select a make to see models</div>}
            {make && (modelsByMake[make] || []).length === 0 && (
              <button onClick={() => { onApply({ make }); setOpen(false); }} className="block w-full text-left px-3 py-2 text-sm text-[#940C2C] font-semibold hover:bg-[#F6E6EB]">All {make}</button>
            )}
            {make && (modelsByMake[make] || []).map((md) => (
              <button key={md} onClick={() => { onApply({ make, model: md }); setOpen(false); }}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-[#F6E6EB] hover:text-[#940C2C]">
                {md}
              </button>
            ))}
            {make && (modelsByMake[make] || []).length > 0 && (
              <button onClick={() => { onApply({ make }); setOpen(false); }} className="block w-full text-left px-3 py-2 text-sm text-[#940C2C] font-bold hover:bg-[#F6E6EB] border-t border-gray-100">All {make} →</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomPricePop = ({ onApply, testid }) => {
  const [open, setOpen] = useState(false);
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-bold border transition-all ${open ? "bg-white text-[#940C2C] border-white" : "bg-transparent text-white border-white/40 hover:bg-white hover:text-[#940C2C] hover:border-white"}`}>
        Price Range <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-2 z-50 min-w-[260px]">
          {PRICE_OPTS.map((p) => (
            <button key={p.label} onClick={() => { onApply(p.qs); setOpen(false); }} className="block w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F6E6EB] hover:text-[#940C2C]">{p.label}</button>
          ))}
          <div className="border-t border-gray-100 mt-2 pt-2 px-2">
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Custom range (₹)</div>
            <div className="flex gap-1.5">
              <input type="number" placeholder="Min" value={min} onChange={(e) => setMin(e.target.value)} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs" data-testid="price-min-input" />
              <input type="number" placeholder="Max" value={max} onChange={(e) => setMax(e.target.value)} className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs" data-testid="price-max-input" />
            </div>
            <button onClick={() => {
              const qs = {}; if (min) qs.price_min = min; if (max) qs.price_max = max;
              onApply(qs); setOpen(false);
            }} className="mt-2 w-full btn-primary !py-1.5 text-xs" data-testid="price-custom-apply">Apply</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [facets, setFacets] = useState({ makes: [], body_types: [], fuels: [] });
  const [modelsByMake, setModelsByMake] = useState({});
  const [settings, setSettings] = useState({});
  const { ids } = useShortlist();

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/vehicles/facets").then((r) => setFacets(r.data || { makes: [] })).catch(() => {});
    api.get("/vehicles?limit=200").then((r) => {
      const map = {};
      (r.data.items || []).forEach((v) => {
        if (!map[v.make]) map[v.make] = new Set();
        if (v.model) map[v.make].add(v.model);
      });
      const out = {}; Object.keys(map).forEach((k) => out[k] = Array.from(map[k]).sort());
      setModelsByMake(out);
    }).catch(() => {});
  }, []);

  const { phone, whatsapp, phoneIsDemo, whatsappIsDemo } = resolveContact(settings);
  const submitSearch = (e) => { e.preventDefault(); navigate(`/buy?search=${encodeURIComponent(q)}`); setOpen(false); };
  const go = (qs) => { const p = new URLSearchParams(qs).toString(); navigate(`/buy?${p}`); };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
      <div className="cs-container flex items-center gap-3 py-3">
        <Link to="/" className="shrink-0" data-testid="header-logo-link">
          <CSLogo className="h-11 w-36 sm:w-40" />
        </Link>
        <div className="hidden xl:flex items-center gap-2 text-sm text-gray-600 border-l border-gray-200 pl-4 ml-1" data-testid="location-selector">
          <MapPin size={16} className="text-[#940C2C]" />
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase text-gray-400 tracking-wider">Location</span>
            <span className="font-semibold text-gray-900 text-sm">Delhi NCR</span>
          </div>
        </div>
        <form onSubmit={submitSearch} className="hidden md:flex flex-1 min-w-0 max-w-lg items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2.5 focus-within:border-gray-900 focus-within:bg-white transition-colors ml-2" data-testid="global-search-form">
          <Search size={18} className="text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by make or model e.g. Swift, Creta…" className="flex-1 min-w-0 bg-transparent text-sm placeholder:text-gray-400" data-testid="global-search-input" />
        </form>
        <div className="hidden lg:flex items-center gap-5 text-sm font-semibold text-gray-800 ml-auto">
          {navLinks.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={n.testid} className={({ isActive }) => `hover:text-[#940C2C] transition-colors ${isActive ? "text-[#940C2C]" : ""}`}>{n.label}</NavLink>
          ))}
          <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
            <button className="flex items-center gap-1 hover:text-[#940C2C]" data-testid="nav-more">More <ChevronDown size={14} /></button>
            {moreOpen && (
              <div className="absolute right-0 top-full pt-2 w-56" data-testid="more-dropdown">
                <div className="bg-white border border-gray-200 rounded-lg shadow-xl py-2">
                  {moreLinks.map((m) => <Link key={m.to} to={m.to} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#940C2C]">{m.label}</Link>)}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 lg:ml-2 shrink-0 whitespace-nowrap">
          <Link to="/shortlist" className="hidden md:inline-flex relative p-2 rounded-full hover:bg-gray-100" data-testid="shortlist-icon-link">
            <Heart size={20} className={ids.length ? "fill-[#940C2C] text-[#940C2C]" : "text-gray-700"} />
            {ids.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#940C2C] text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center font-bold">{ids.length}</span>}
          </Link>
          <a href={buildWhatsAppLink(whatsapp, "Hi Carz Swag!")} target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 relative rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-sm px-3.5 py-2" data-testid="header-whatsapp-cta">
            <MessageCircle size={14} /> WhatsApp {whatsappIsDemo && <DemoDot />}
          </a>
          <a href={`tel:${phone}`} className="hidden sm:inline-flex items-center gap-1.5 relative rounded-lg bg-[#940C2C] hover:bg-[#7A0A24] text-white font-semibold text-sm px-3.5 py-2" data-testid="header-call-cta">
            <Phone size={14} /> Call Us {phoneIsDemo && <DemoDot />}
          </a>
          <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-md border border-gray-200" data-testid="mobile-menu-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>

      {/* RED EXPLORE BY BAR */}
      <div className="hidden lg:block" style={{ backgroundColor: "#940C2C" }}>
        <div className="cs-container flex items-center gap-3 py-3">
          <span className="text-[11px] font-extrabold text-white uppercase tracking-widest shrink-0 opacity-90">Explore By</span>
          <CustomPricePop onApply={go} testid="disc-price" />
          <MakeModelPop makes={facets.makes || []} modelsByMake={modelsByMake} onApply={go} testid="disc-make" />
          <Pop label="Fuel" testid="disc-fuel">
            {FUEL_OPTS.map((f) => <button key={f} onClick={() => go({ fuel: f })} className="block w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F6E6EB] hover:text-[#940C2C]">{f}</button>)}
          </Pop>
          <Pop label="KM Driven" testid="disc-km">
            {KM_OPTS.map((k) => <button key={k.label} onClick={() => go(k.qs)} className="block w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F6E6EB] hover:text-[#940C2C]">{k.label}</button>)}
          </Pop>
          <Pop label="Transmission" testid="disc-trans">
            {TRANS_OPTS.map((t) => <button key={t} onClick={() => go({ transmission: t })} className="block w-full text-left px-3 py-2 text-sm rounded hover:bg-[#F6E6EB] hover:text-[#940C2C]">{t}</button>)}
          </Pop>
          <Link to="/buy?certified=true" className="ml-auto text-xs font-bold text-white hover:text-white/80 inline-flex items-center gap-1">CS Certified <span>→</span></Link>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu-panel">
          <form onSubmit={submitSearch} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full mx-4 my-3 px-4 py-2.5">
            <Search size={18} className="text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search cars…" className="flex-1 bg-transparent text-sm" />
          </form>
          <nav className="px-4 pb-4 flex flex-col divide-y divide-gray-100">
            {[...navLinks, ...moreLinks].map((n) => (<Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-3 text-sm font-semibold text-gray-800">{n.label}</Link>))}
            <Link to="/shortlist" onClick={() => setOpen(false)} className="py-3 text-sm font-semibold text-gray-800">Shortlisted ({ids.length})</Link>
          </nav>
        </div>
      )}
    </header>
  );
};

const DemoDot = () => (
  <span className="ml-0.5 inline-flex items-center bg-black/25 text-[9px] font-bold uppercase tracking-wider rounded px-1 py-0.5" title="Preview placeholder — set real number in Admin → Settings">DEMO</span>
);
